import React from 'react';
import PropTypes from 'prop-types';
import { InputText, Dropdown, InputTextarea } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import { PetDetailsRadios } from '../../petDetailRadios/petDetailsRadios';
import { ChatTypeSelection } from './chatTypeSelection';
import { PetGenderSelection } from './petGenderSelection';
import { useLiveVetForm } from './useLiveVetForm';
import { AU_MOBILE_REGEX, PET_SPECIES, EMAIL_REGEX } from '../../../constants/constants';
import { FORM_KEYS } from '../../../constants/formKeys';
import { isRequired } from '../../../utils/validationUtils';

import '../liveVet.scss';

export const LiveVetForm = ({ setConsultationUrl, isReadOnly = false }) => {
  const {
    petDetails,
    formContext,
    chatType,
    petGender,
    petDetailsLoading,
    failedToStartConsultation,
    fullPolicyNumber,
    isLiteModeEnabled,
    getValues,
    handleSubmit,
    onFormSubmit,
    handlePetSelection
  } = useLiveVetForm(setConsultationUrl);

  return (
    <form
      onSubmit={handleSubmit(onFormSubmit)}
      noValidate // ensures that only react-hook-form rules are applied, not the browser’s native validation messages
    >
      <div className="heading-content">
        <h4>
          As an added perk to having a policy with us, you get complimentary access to vet care, anytime, anywhere.
        </h4>
        <h6>Simply complete the below steps and connect to a vet via chat or video call within minutes.</h6>
      </div>
      {(isLiteModeEnabled || !petDetailsLoading) && (
        <div>
          <ol className="styledCounter leftAlign medium numberCircle">
            <li>
              <ChatTypeSelection
                chatType={chatType}
                formContext={formContext}
              />
            </li>
            {!isLiteModeEnabled && (
              <li>
                <h3>This vet consult is for</h3>
                {petDetails && (
                  <PetDetailsRadios
                    value={fullPolicyNumber}
                    petDetails={petDetails}
                    isIconOnly
                    isReadOnly={isReadOnly}
                    onChange={handlePetSelection}
                  />
                )}
              </li>
            )}
            <li>
              <h3>{isLiteModeEnabled ? "Pet's details" : `${getValues(FORM_KEYS.PET_NAME)}'s details`}</h3>
              <fieldset className="in-line-input">
                <InputText
                  fieldFormContext={formContext}
                  name={FORM_KEYS.PET_NAME}
                  label="Pet's name"
                  validation={{
                    required: isRequired('A value is required for this field')
                  }}
                  inputCustomCssClass={!isLiteModeEnabled ? 'custom-disabled-input' : ''}
                  disabled={!isLiteModeEnabled}
                />
                <Dropdown
                  fieldFormContext={formContext}
                  label="Pet species"
                  name={FORM_KEYS.PET_SPECIES}
                  validation={{
                    required: isRequired('A value is required for this field')
                  }}
                  inputCustomCssClass={!isLiteModeEnabled ? 'custom-disabled-input' : ''}
                  disabled={!isLiteModeEnabled}
                  options={PET_SPECIES}
                />
              </fieldset>
              <fieldset className="in-line-input">
                <InputText
                  fieldFormContext={formContext}
                  name={FORM_KEYS.PET_BREED}
                  label="Pet breed"
                  validation={{
                    required: isRequired('A value is required for this field')
                  }}
                  inputCustomCssClass={!isLiteModeEnabled ? 'custom-disabled-input' : ''}
                  disabled={!isLiteModeEnabled}
                />
                <InputText
                  fieldFormContext={formContext}
                  name={FORM_KEYS.PET_AGE}
                  label="Pet age (years)"
                  validation={{
                    required: isRequired('A value is required for this field')
                  }}
                  inputCustomCssClass={!isLiteModeEnabled ? 'custom-disabled-input' : ''}
                  disabled={!isLiteModeEnabled}
                />
              </fieldset>

              <fieldset className="petGenderContainer">
                <PetGenderSelection
                  petGender={petGender}
                  formContext={formContext}
                  isDisabled={!isLiteModeEnabled}
                />
              </fieldset>
              <fieldset className="concernSummaryContainer">
                <InputTextarea
                  label="How can the Vet help you today?"
                  name={FORM_KEYS.CONCERN_SUMMARY}
                  fieldFormContext={formContext}
                  validation={{
                    required: isRequired('Please provide reason for live vet consultation.')
                  }}
                />
              </fieldset>
            </li>
            <li>
              <h3>Your details</h3>
              <fieldset className="in-line-input">
                <InputText
                  fieldFormContext={formContext}
                  name={FORM_KEYS.FIRST_NAME}
                  label="First name"
                  validation={{
                    required: isRequired('A value is required for this field')
                  }}
                  disabled={!isLiteModeEnabled}
                  inputCustomCssClass={!isLiteModeEnabled ? 'custom-disabled-input' : ''}
                />
                <InputText
                  fieldFormContext={formContext}
                  name={FORM_KEYS.LAST_NAME}
                  label="Last Name"
                  validation={{
                    required: isRequired('A value is required for this field')
                  }}
                  disabled={!isLiteModeEnabled}
                  inputCustomCssClass={!isLiteModeEnabled ? 'custom-disabled-input' : ''}
                />
              </fieldset>
              <fieldset className="in-line-input">
                <InputText
                  fieldFormContext={formContext}
                  name={FORM_KEYS.EMAIL_ADDRESS}
                  label="Email Address"
                  validation={{
                    required: isRequired('Email address is invalid.'),
                    pattern: {
                      value: EMAIL_REGEX,
                      message: 'Email address is invalid.'
                    }
                  }}
                />
                <InputText
                  fieldFormContext={formContext}
                  name={FORM_KEYS.MOBILE_NUMBER}
                  label="Phone"
                  validation={{
                    required: isRequired('Please enter a phone number.'),
                    minLength: {
                      value: 10,
                      message: 'Must be 10 digits in length.'
                    },
                    pattern: {
                      value: AU_MOBILE_REGEX,
                      message: 'Must begin with 04.'
                    }
                  }}
                  maxLength={10}
                />
              </fieldset>
            </li>
          </ol>
          {failedToStartConsultation && (
            <div className="error-message-container">
              <span
                className="error-message"
                aria-live="assertive"
                role="alert"
              >
                We encountered an error while starting live vet session. Please come back later and try again.
              </span>
            </div>
          )}
          <div className="start-chat-button">
            <button
              type="submit"
              className="button primary"
            >
              <span className="labelText">Start Chat</span>
            </button>
          </div>
        </div>
      )}
    </form>
  );
};

LiveVetForm.propTypes = {
  setConsultationUrl: PropTypes.func,
  isReadOnly: PropTypes.bool
};
